import { pointsFor, type RoundEntry, type Score } from '../model/types.js';
import { bakuPlan, bakuTrfRecords } from '../pairing/acceleration.js';
import { trfScoringFromPointSystem } from '../trf/scoring.js';
import { emptyTrf, type TrfDocument, type TrfPlayer } from '../trf/types.js';
import type { Entrant, Tournament } from './types.js';

/** A player's per-round history, indexed by round (0 = round 1). */
export function playerHistory(t: Tournament, player: Entrant, upToRound?: number): RoundEntry[] {
  const byId = new Map(t.players.map((p) => [p.id, p]));
  const last = Math.min(upToRound ?? t.rounds.length, t.rounds.length);
  const out: RoundEntry[] = [];
  for (let i = 0; i < last; i++) {
    const r = t.rounds[i];
    if (r === undefined) break;
    let entry: RoundEntry | null = null;
    for (const g of r.games) {
      if (g.white === player.id) {
        const opp = byId.get(g.black);
        entry = { opponent: opp?.pairingNumber ?? null, colour: 'w', result: g.result.white };
        break;
      }
      if (g.black === player.id) {
        const opp = byId.get(g.white);
        entry = { opponent: opp?.pairingNumber ?? null, colour: 'b', result: g.result.black };
        break;
      }
    }
    if (entry === null) {
      const bye = r.byes.find((b) => b.player === player.id);
      entry = { opponent: null, colour: null, result: bye?.kind ?? 'Z' };
    }
    out.push(entry);
  }
  return out;
}

/** Histories of every player, keyed by pairing number. */
export function allHistories(t: Tournament, upToRound?: number): Map<number, RoundEntry[]> {
  const m = new Map<number, RoundEntry[]>();
  for (const p of t.players) m.set(p.pairingNumber, playerHistory(t, p, upToRound));
  return m;
}

export function scoreOf(t: Tournament, history: readonly RoundEntry[]): Score {
  let s = 0;
  for (const r of history) s += pointsFor(r.result, t.settings.pointSystem);
  return s;
}

/** Is the player expected to be paired in the given round (1-based)? */
export function isAvailable(p: Entrant, round: number): boolean {
  if (p.withdrawnAfterRound !== null && round > p.withdrawnAfterRound) return false;
  return !p.byeRequests.some((b) => b.round === round);
}

/** Export the tournament as a TRF document (TRF-16 layout with engine extensions). */
export function toTrf(
  t: Tournament,
  options: { upToRound?: number; ranks?: ReadonlyMap<number, number> } = {},
): TrfDocument {
  const s = t.settings;
  const histories = allHistories(t, options.upToRound);
  const players: TrfPlayer[] = [...t.players]
    .sort((a, b) => a.pairingNumber - b.pairingNumber)
    .map((p) => {
      const rounds = histories.get(p.pairingNumber) ?? [];
      return {
        startRank: p.pairingNumber,
        sex: p.sex ?? null,
        title: p.title ?? '',
        name: p.name,
        rating: p.fideRating ?? p.rating,
        federation: p.federation ?? '',
        fideId: p.fideId ?? '',
        birthDate: p.birthDate ?? '',
        points: scoreOf(t, rounds),
        rank: options.ranks?.get(p.pairingNumber) ?? 0,
        rounds,
      };
    });
  const headers = new Map<string, string>();
  const put = (code: string, v: string): void => {
    if (v !== '') headers.set(code, v);
  };
  put('012', s.name);
  put('022', s.city);
  put('032', s.federation);
  put('042', s.startDate);
  put('052', s.endDate);
  put('062', String(t.players.length));
  put('072', String(t.players.filter((p) => (p.fideRating ?? 0) > 0).length));
  put('092', systemLabel(s.system));
  put('102', s.chiefArbiter);
  put('112', s.deputyArbiters.join(', '));
  put('122', s.timeControl);
  return {
    ...emptyTrf(),
    headers,
    players,
    totalRounds: s.rounds,
    initialColour: s.initialColour,
    scoring: trfScoringFromPointSystem(s.pointSystem),
    tournamentType: trfTournamentType(s.system, s.acceleration),
    accelerations:
      s.acceleration === 'baku'
        ? bakuTrfRecords(bakuPlan(t.players.length, s.rounds), s.pointSystem)
        : [],
    tiebreaks: s.tiebreaks.map((x) => x.replace(':', '/')),
  };
}

/** ETT26 code (TRF record 192) for a pairing system. */
export function trfTournamentType(system: string, acceleration: string): string | null {
  const baku = acceleration === 'baku' ? '_BAKU' : '';
  switch (system) {
    case 'dutch':
      return `FIDE_DUTCH_2026${baku}`;
    case 'dubov':
      return `FIDE_DUBOV${baku}`;
    case 'burstein':
      return `FIDE_BURSTEIN${baku}`;
    case 'double-swiss':
      return `FIDE_DOUBLESWISS${baku}`;
    case 'round-robin':
      return 'FIDE_ROUNDROBIN';
    case 'double-round-robin':
      return 'FIDE_DOUBLEROUNDROBIN';
    case 'scheveningen':
      return 'FIDE_SCHEVENINGEN';
    case 'knockout':
      return 'CUSTOM_KNOCKOUT';
    case 'monrad':
    case 'uscf':
    case 'keizer':
    case 'lim':
      return 'CUSTOM_SWISS';
    default:
      return null;
  }
}

function systemLabel(system: string): string {
  switch (system) {
    case 'dutch':
      return 'Swiss System (FIDE Dutch)';
    case 'burstein':
      return 'Swiss System (Burstein)';
    case 'dubov':
      return 'Swiss System (Dubov)';
    case 'lim':
      return 'Swiss System (Lim)';
    case 'uscf':
      return 'Swiss System (US Chess)';
    case 'round-robin':
      return 'Round Robin';
    case 'double-round-robin':
      return 'Double Round Robin';
    default:
      return system;
  }
}
